import { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { AuthContextType, User } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<{ id: string; name: string; createdBy: string; createdAt: Timestamp; updatedAt: Timestamp; } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          // Même si le document n'existe pas, on garde l'utilisateur Firebase
          const userData = userDoc.exists() ? userDoc.data() : {};
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            ...userData,
          } as User);

          // Charger les données de l'organisation si l'utilisateur en a une
          if (userData.organizationId) {
            try {
              const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
              if (orgDoc.exists()) {
                setOrganization({
                  id: orgDoc.id,
                  ...orgDoc.data(),
                } as { id: string; name: string; createdBy: string; createdAt: Timestamp; updatedAt: Timestamp; });
              }
            } catch (orgErr) {
              console.error('Error fetching organization data:', orgErr);
              // Ne pas bloquer l'authentification si l'organisation ne peut pas être chargée
            }
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          // Garder l'utilisateur Firebase même en cas d'erreur
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
          } as User);
          setError('Error fetching additional user data');
        }
      } else {
        setUser(null);
        setOrganization(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error('Sign in error:', err);
      setError('Failed to sign in');
      throw err;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    try {
      setError(null);
      const { user: firebaseUser } = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Document utilisateur principal
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        firstName,
        lastName,
        email: email.toLowerCase(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Initialiser les préférences de notification
      await setDoc(doc(db, 'users', firebaseUser.uid, 'settings', 'notifications'), {
        enabled: false,
        maintenance: true,
        fuel: true,
        mileage: true,
        updatedAt: new Date().toISOString()
      });

    } catch (err) {
      console.error('Sign up error:', err);
      setError('Failed to create account');
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      setUser(null);
      setOrganization(null);
    } catch (err) {
      console.error('Sign out error:', err);
      setError('Failed to sign out');
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to reset password');
      throw err;
    }
  };

  const value = {
    user,
    currentUser: user, // Alias pour la compatibilité
    currentOrganization: organization,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
